import { X } from 'lucide-react'
import { cn } from '@/lib/cn'

export interface ShoutTickerProps {
  body: string
  /** Who it is from, on the band's left edge: "From Skyler". */
  from: string
  /** The same, short enough for a phone: "Skyler". */
  short: string
  /** Hides it from Today; left out on the Settings preview. */
  onDismiss?: () => void
  /** Drop in on mount. Off for the Settings preview, which changes as you type. */
  dropIn?: boolean
  className?: string
}

/** Copies of the message in each half of the track: enough to fill a wide screen. */
const REPEATS = 4

/**
 * A partner's shout as a stadium ticker: a solid red band with the message scrolling across on
 * a loop. The track holds the message twice over and slides by half, so the loop has no seam.
 * Under prefers-reduced-motion it holds still (globals.css).
 */
export function ShoutTicker({
  body,
  from,
  short,
  onDismiss,
  dropIn = true,
  className,
}: ShoutTickerProps) {
  // About 70 px a second whatever the length, so a long line doesn't race by.
  const seconds = Math.max(10, Math.round(((body.length + 6) * REPEATS * 11) / 70))
  const half = (
    <div className="flex shrink-0 items-center">
      {Array.from({ length: REPEATS }, (_, i) => (
        <span key={i} className="flex shrink-0 items-center">
          <span className="px-6">{body}</span>
          <span className="size-1.5 shrink-0 rounded-full bg-white/55" />
        </span>
      ))}
    </div>
  )
  return (
    <div
      role="status"
      className={cn(
        'relative flex h-11 items-stretch overflow-hidden bg-shout text-white',
        className,
      )}
      style={dropIn ? { animation: 'gd-band-in 0.5s var(--ease-out) both' } : undefined}
    >
      <span className="sr-only">
        {from}: {body}
      </span>
      <span
        aria-hidden="true"
        className="relative z-10 flex shrink-0 items-center bg-[var(--shout-deep)] px-3 text-caption font-bold whitespace-nowrap"
      >
        <span className="max-[479px]:hidden">{from}</span>
        <span className="min-[480px]:hidden">{short}</span>
      </span>
      <div aria-hidden="true" className="min-w-0 flex-1 overflow-hidden">
        <div
          className="flex h-full w-max items-center font-display text-[1.0625rem] font-black tracking-[0.01em] whitespace-nowrap italic"
          style={{ animation: `gd-ticker ${seconds}s linear infinite` }}
        >
          {half}
          {half}
        </div>
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss message"
          className="relative z-10 flex w-11 shrink-0 items-center justify-center bg-[var(--shout-deep)] text-white/85 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-white"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}
